import {
  AfterViewInit,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  Renderer2,
  ViewChild,
} from '@angular/core';
import { SpeechMarkup } from '../../../../../shared/src/types/speech-markup';
import WaveSurfer from 'wavesurfer.js';
import { splitTitledText } from './split-titled-text';

interface WordSpan {
  text: string;
  element?: HTMLElement;
  timeSec?: number;
  isTitle?: boolean;
}

@Component({
  selector: 'app-text-player',
  imports: [],
  templateUrl: './text-player.component.html',
  styleUrls: ['./text-player.component.scss'],
})
export class TextPlayerComponent implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild('textContainer') textContainer!: ElementRef<HTMLDivElement>;
  @ViewChild('waveform') waveformEl!: ElementRef;

  @Input() hasTitle: boolean = false;

  // Inputs
  @Input() fullText: string = '';
  @Input() audioFile: string = '';
  @Input() markups: SpeechMarkup[] = [];

  @Output() audioProgressed = new EventEmitter<number>();

  // Audio and timing
  isAudioPlaying = false;
  wordSpans: WordSpan[] = [];
  currentHighlightedElement: HTMLElement | null = null;
  timeObserver: any;
  wavesurfer: WaveSurfer;

  // Animation state
  hasStartedPlaying = false;
  audioFinished = false;

  // Expected audio duration
  private audioDuration: number = 0;

  constructor(private renderer: Renderer2) {}

  ngOnInit(): void {
    // Process markup data and add computed timeSec
    this.markups = this.markups.map(markup => ({
      ...markup,
      timeSec: markup.time / 1000,
    }));

    // Calculate expected audio duration from markups
    if (this.markups.length > 0) {
      const maxTime = Math.max(...this.markups.map(markup => markup.time));
      this.audioDuration = maxTime / 1000 + 2; // Add 2 seconds buffer
    }
  }

  ngAfterViewInit(): void {
    // Reset animation state when component is initialized
    this.resetState();

    // Render text with spans for each word
    this.renderTextWithSpans();

    // Initialize waveform
    this.initWaveform();

    // Setup progress tracking after wavesurfer is ready
    this.setupProgressTracking();
  }

  ngOnDestroy(): void {
    if (this.timeObserver) {
      cancelAnimationFrame(this.timeObserver);
    }

    if (this.wavesurfer) {
      this.wavesurfer.destroy();
    }
  }

  startPlayback(): void {
    if (!this.isAudioPlaying) {
      this.playText();
    }
  }

  /**
   * Renders the text with spans for each word that can be highlighted
   */
  renderTextWithSpans(): void {
    if (!this.textContainer) return;

    const container = this.textContainer.nativeElement;

    // Clear existing content
    container.innerHTML = '';

    const { title, paragraphs } = splitTitledText(this.fullText, this.hasTitle);

    if (title !== undefined) {
      // Create title element with spans for each word
      const titleElement = this.renderer.createElement('h1');
      this.renderer.addClass(titleElement, 'title');

      // Add title text with spans
      this.addSpannedText(titleElement, title, true);
      this.renderer.appendChild(container, titleElement);
    }

    paragraphs.forEach(paragraph => {
      const p = this.renderer.createElement('p');
      this.addSpannedText(p, paragraph, false);
      this.renderer.appendChild(container, p);
    });
  }

  /**
   * Adds text to an element with each word wrapped in a span
   */
  addSpannedText(element: HTMLElement, text: string, isTitle: boolean): void {
    // First, split the text into word tokens preserving spaces and punctuation
    const tokens = this.tokenizeText(text);

    tokens.forEach(token => {
      if (token.type === 'space') {
        // For spaces/whitespace, just add a text node
        this.renderer.appendChild(element, this.renderer.createText(token.text));
      } else {
        // For actual words, create a span
        const span = this.renderer.createElement('span');
        this.renderer.addClass(span, 'word');

        // Add a special "placeholder" element to prevent layout shifts
        const placeholder = this.renderer.createElement('span');
        this.renderer.addClass(placeholder, 'placeholder');
        this.renderer.appendChild(placeholder, this.renderer.createText(token.text));
        this.renderer.appendChild(span, placeholder);

        // Add actual visible text
        const textSpan = this.renderer.createElement('span');
        this.renderer.addClass(textSpan, 'text');
        this.renderer.appendChild(textSpan, this.renderer.createText(token.text));
        this.renderer.appendChild(span, textSpan);

        this.renderer.appendChild(element, span);

        // Store this span in our list of words
        this.wordSpans.push({
          text: token.text,
          element: span,
          isTitle: isTitle,
        });
      }
    });
  }

  /**
   * Tokenize text into words, spaces and punctuation
   */
  tokenizeText(text: string): { type: 'word' | 'space'; text: string }[] {
    // This regex handles hyphenated words better - it keeps them together
    const regex = /(\s+)|([^\s]+\-[^\s]+)|([^\s]+)/g;
    const tokens: { type: 'word' | 'space'; text: string }[] = [];
    let match;

    while ((match = regex.exec(text)) !== null) {
      if (match[1]) {
        // Space group
        tokens.push({ type: 'space', text: match[1] });
      } else if (match[2] || match[3]) {
        // Word or hyphenated word
        tokens.push({ type: 'word', text: match[2] || match[3] });
      }
    }

    return tokens;
  }

  /**
   * Clean a word by removing punctuation and converting to lowercase
   * This helps with matching words regardless of punctuation
   */
  cleanWord(word: string): string {
    return word
      .replace(/[.,!?;:'"()]/g, '')
      .toLowerCase()
      .trim();
  }

  /**
   * Associate time information with each word span in sequence
   */
  mapTimesToWordSpans(): void {
    // Create a separate list of title and body spans for better matching
    const titleSpans = this.wordSpans.filter(span => span.isTitle);
    const bodySpans = this.wordSpans.filter(span => !span.isTitle);

    // Add clean version of text for comparison
    titleSpans.forEach(span => {
      (span as any).cleanText = this.cleanWord(span.text);
    });

    bodySpans.forEach(span => {
      (span as any).cleanText = this.cleanWord(span.text);
    });

    // Markups should be processed in order
    const sortedMarkups = [...this.markups].sort((a, b) => a.time - b.time);

    // Determine how many words are in the title and map exactly that many markups to the title
    const titleWordCount = titleSpans.length;
    const titleMarkups = sortedMarkups.slice(0, titleWordCount);
    const bodyMarkups = sortedMarkups.slice(titleWordCount);

    // Track matches
    let fuzzyMatches = 0;

    // Process title markups
    let titleIndex = 0;
    for (const markup of titleMarkups) {
      const word = markup.text;
      const cleanWord = this.cleanWord(word);
      const timeSec = markup.timeSec || markup.time / 1000;
      let found = false;

      // Try matching with clean text (ignoring punctuation)
      for (let i = titleIndex; i < titleSpans.length; i++) {
        const span = titleSpans[i];
        if ((span as any).cleanText === cleanWord) {
          span.timeSec = timeSec;
          titleIndex = i + 1;
          found = true;
          fuzzyMatches++;
          break;
        }
      }
    }

    // Process body markups
    let bodyIndex = 0;
    for (const markup of bodyMarkups) {
      const word = markup.text;
      const cleanWord = this.cleanWord(word);
      const timeSec = markup.timeSec || markup.time / 1000;
      let found = false;

      // Special handling for hyphenated words
      const isHyphenated = word.includes('-');

      // Try matching with clean text (ignoring punctuation)
      for (let i = bodyIndex; i < bodySpans.length; i++) {
        const span = bodySpans[i];
        if (!span.timeSec && span.timeSec !== 0 && (span as any).cleanText === cleanWord) {
          span.timeSec = timeSec;
          bodyIndex = i + 1;
          found = true;
          fuzzyMatches++;
          break;
        }
      }

      // For hyphenated words, try matching parts
      if (!found && isHyphenated) {
        const parts = cleanWord.split('-');
        let startIndex = bodyIndex;

        // Try to find the first part
        for (let i = startIndex; i < bodySpans.length; i++) {
          const span = bodySpans[i];
          if (!span.timeSec && span.timeSec !== 0 && (span as any).cleanText === parts[0]) {
            span.timeSec = timeSec;
            startIndex = i + 1;
            found = true;
            fuzzyMatches++;
            break;
          }
        }

        // Try to find the second part if we found the first
        if (found && parts.length > 1) {
          for (let i = startIndex; i < bodySpans.length; i++) {
            const span = bodySpans[i];
            if (!span.timeSec && span.timeSec !== 0 && (span as any).cleanText === parts[1]) {
              span.timeSec = timeSec;
              bodyIndex = i + 1;
              fuzzyMatches++;
              break;
            }
          }
        }
      }

      // Try substring match as last resort
      if (!found) {
        for (let i = bodyIndex; i < bodySpans.length; i++) {
          const span = bodySpans[i];
          if (
            !span.timeSec &&
            span.timeSec !== 0 &&
            ((span as any).cleanText.includes(cleanWord) ||
              cleanWord.includes((span as any).cleanText))
          ) {
            span.timeSec = timeSec;
            bodyIndex = i + 1;
            found = true;
            fuzzyMatches++;
            break;
          }
        }
      }
    }
  }

  /**
   * Setup tracking of audio progress for highlighting text
   */
  setupProgressTracking(): void {
    // Wait for the next frame to ensure DOM is fully rendered
    requestAnimationFrame(() => {
      // Map times to word spans
      this.mapTimesToWordSpans();

      const updateHighlight = () => {
        if (this.isAudioPlaying && this.wavesurfer) {
          const currentTime = this.wavesurfer.getCurrentTime();

          // Check if audio is near the end
          if (
            this.audioDuration > 0 &&
            currentTime >= this.audioDuration - 0.1 &&
            !this.audioFinished
          ) {
            this.handleAudioEnd();
            return;
          }

          // Highlight the current word
          this.highlightWordAtTime(currentTime);

          // Report progress to parent component
          this.audioProgressed.emit(
            this.wavesurfer.getDuration() > 0 ? currentTime / this.wavesurfer.getDuration() : 0
          );
        }

        // Continue observing
        this.timeObserver = requestAnimationFrame(updateHighlight);
      };

      // Start observing
      this.timeObserver = requestAnimationFrame(updateHighlight);
    });
  }

  /**
   * Highlight the word at the specified time
   */
  highlightWordAtTime(timeInSeconds: number): void {
    const toleranceThreshold = 0.25; // Tolerance window in seconds

    // Find the word span with time closest to current time
    let closestSpan: WordSpan | null = null;
    let closestTimeDiff = Number.MAX_VALUE;

    for (const span of this.wordSpans) {
      if (span.timeSec || span.timeSec === 0) {
        const timeDiff = Math.abs(span.timeSec - timeInSeconds);

        if (timeDiff <= toleranceThreshold && timeDiff < closestTimeDiff) {
          closestTimeDiff = timeDiff;
          closestSpan = span;
        }
      }
    }

    // If we found a span and it's not the currently highlighted one, update highlighting
    if (
      closestSpan &&
      closestSpan.element &&
      closestSpan.element !== this.currentHighlightedElement
    ) {
      // Clear previous highlight
      this.clearHighlight();

      // Set new highlight
      this.currentHighlightedElement = closestSpan.element;

      if (this.currentHighlightedElement) {
        this.renderer.addClass(this.currentHighlightedElement, 'highlighted');
      }
    }
  }

  /**
   * Clear the current text highlight
   */
  clearHighlight(): void {
    if (this.currentHighlightedElement) {
      this.renderer.removeClass(this.currentHighlightedElement, 'highlighted');
      this.currentHighlightedElement = null;
    }
  }

  /**
   * Start playback
   */
  playText(): void {
    try {
      if (!this.wavesurfer) {
        console.error('WaveSurfer not initialized');
        return;
      }

      this.hasStartedPlaying = true;
      this.isAudioPlaying = true;

      // Start WaveSurfer playback
      this.wavesurfer.play();
    } catch (error) {
      console.error('Error starting audio playback:', error);
    }
  }

  /**
   * Handle audio end
   */
  handleAudioEnd(): void {
    // Check if we've already processed the ending
    if (this.audioFinished) {
      return; // Already finished, don't process again
    }

    // Clear any remaining highlighting
    this.clearHighlight();

    // Set playing state to false
    this.isAudioPlaying = false;

    // Mark audio as finished
    this.audioFinished = true;

    // Ensure wavesurfer is stopped
    if (this.wavesurfer) {
      this.wavesurfer.stop();
    }

    // Emit 100% progress
    this.audioProgressed.emit(1);
  }

  /**
   * Reset the component state
   */
  resetState(): void {
    this.hasStartedPlaying = false;
    this.audioFinished = false;
    this.isAudioPlaying = false;
    this.clearHighlight();

    if (this.wavesurfer) {
      try {
        this.wavesurfer.stop();
        this.wavesurfer.seekTo(0);
      } catch (e) {
        console.error('Error resetting wavesurfer:', e);
      }
    }
  }

  /**
   * Public method to reset the player state
   * Can be called from parent components via ViewChild
   */
  public reset(): void {
    this.resetState();
  }

  /**
   * Initialize WaveSurfer
   */
  initWaveform(): void {
    this.wavesurfer = WaveSurfer.create({
      container: this.waveformEl.nativeElement,
      height: 48,
      barWidth: 2,
      barGap: 2,
      barRadius: 2,
      cursorWidth: 0,
      waveColor: '#bdbec0',
      progressColor: '#31BC6D',
      url: this.audioFile,
      interact: false,
      backend: 'MediaElement', // better for iOS
    });

    // Set up event listeners
    this.setupWavesurferEvents();
  }

  /**
   * Set up WaveSurfer event listeners
   */
  setupWavesurferEvents(): void {
    //trying to force a redraw
    setTimeout(() => {
      this.wavesurfer.setVolume(0.99);
      this.wavesurfer.setVolume(1);
    }, 100);

    // When audio is ready to play
    this.wavesurfer.on('ready', () => {
      console.log('WaveSurfer ready');

      setTimeout(() => {
        this.wavesurfer.setVolume(0.99);
        this.wavesurfer.setVolume(1);
      }, 100);
    });

    // When audio starts playing
    this.wavesurfer.on('play', () => {
      this.isAudioPlaying = true;
    });

    // When audio is paused
    this.wavesurfer.on('pause', () => {
      this.isAudioPlaying = false;
    });

    // When audio ends
    this.wavesurfer.on('finish', () => {
      this.handleAudioEnd();
    });

    // Handle errors
    this.wavesurfer.on('error', err => {
      console.error('WaveSurfer error:', err);
    });
  }
}
